import { useCallback, useMemo } from 'react';
import type { InternalTableSchema } from '../core/schema/types';

export type AsyncCellLookupFn = (
  field: string,
  value: unknown,
  row: Record<string, unknown>,
) => Promise<Record<string, unknown> | null>;

interface UseTableEditingParams {
  internalSchema: InternalTableSchema;
  tableState: {
    editingRows: Map<string, Record<string, unknown>>;
    editErrors: Map<string, Record<string, string>>;
    serverErrors: Map<string, Record<string, string>>;
  };
  stateActions: {
    startEditing: (rowKey: string, initialValues: Record<string, unknown>) => void;
    startEditingBatch: (
      entries: Array<{ rowKey: string; initialValues: Record<string, unknown> }>
    ) => void;
    updateEditValue: (rowKey: string, field: string, value: unknown) => void;
    patchEditRow: (rowKey: string, values: Record<string, unknown>) => void;
    cancelEditing: (rowKey: string) => void;
    cancelAllEditing: () => void;
    setRowEditErrors?: (rowKey: string, errors: Record<string, string>) => void;
  };
  displayData: unknown[];
  resolveRowKey: (row: Record<string, unknown>, rowIndex: number) => string;
  applyComputeRules: (
    row: Record<string, unknown>,
    changedField: string
  ) => Record<string, unknown>;
  patchEditedRow?: (
    row: Record<string, unknown>,
    context: { field: string; value: unknown }
  ) => Record<string, unknown>;
  validateEditedRow?: (row: Record<string, unknown>, rowKey?: string) => Record<string, string>;
  validationTrigger?: 'save' | 'change';
  asyncCellLookup?: AsyncCellLookupFn;
}

interface UseTableEditingReturn {
  buildWholeRowEditEntries: (
    rowKey: string,
    row: Record<string, unknown>
  ) => Array<{ rowKey: string; initialValues: Record<string, unknown> }>;
  getSubRowKeysForParent: (parentKey: string) => string[];
  handleCellDoubleClick: (rowKey: string, field: string) => void;
  handleEditChange: (rowKey: string, field: string, value: unknown) => void;
  handleEditBlur: (rowKey: string, field: string) => void;
  subRowDataField: string | undefined;
  handleSubRowCellDoubleClick: (parentKey: string, subIndex: number, field: string) => void;
  handleSubRowEditChange: (
    parentKey: string,
    subIndex: number,
    field: string,
    value: unknown
  ) => void;
  handleSubRowEditBlur: (parentKey: string, subIndex: number, field: string) => void;
  editingSubRows: Map<string, Record<string, unknown>>;
  subRowEditErrors: Map<string, Record<string, string>>;
}

export function useTableEditing({
  internalSchema,
  tableState,
  stateActions,
  displayData,
  resolveRowKey,
  applyComputeRules,
  patchEditedRow,
  validateEditedRow,
  validationTrigger = 'save',
  asyncCellLookup,
}: UseTableEditingParams): UseTableEditingReturn {
  const legacyEscapeValidate = internalSchema.features.editing.validateEditedRow;
  const shouldValidateOnChange =
    validateEditedRow != null && (validationTrigger === 'change' || legacyEscapeValidate != null);
  const buildWholeRowEditEntries = useCallback(
    (rowKey: string, row: Record<string, unknown>) => {
      // Delta-only edit state — editors fall back to row[field] for display.
      // Full-row snapshots overwrite fresh displayData with stale nulls on save.
      const entries: Array<{ rowKey: string; initialValues: Record<string, unknown> }> = [
        { rowKey, initialValues: {} },
      ];
      const subField = internalSchema.features.rows.subRows?.dataField;
      if (!subField) return entries;
      const subRows = row[subField] as unknown[] | undefined;
      const hasEditableSubCols = internalSchema.subRowColumns.some((c) => c.editable != null);
      if (!subRows || !hasEditableSubCols) return entries;
      subRows.forEach((sr, idx) => {
        entries.push({
          rowKey: `${rowKey}.${idx}`,
          initialValues: {},
        });
      });
      return entries;
    },
    [internalSchema.features.rows.subRows?.dataField, internalSchema.subRowColumns]
  );

  const getSubRowKeysForParent = useCallback(
    (parentKey: string) => {
      const keys: string[] = [];
      tableState.editingRows.forEach((_v, k) => {
        if (k.startsWith(`${parentKey}.`)) keys.push(k);
      });
      return keys;
    },
    [tableState.editingRows]
  );

  const handleCellDoubleClick = useCallback(
    (rowKey: string, _field: string) => {
      if (!tableState.editingRows.has(rowKey)) {
        const row = displayData.find((r, idx) => {
          const rec = r as Record<string, unknown>;
          return resolveRowKey(rec, idx) === rowKey;
        }) as Record<string, unknown> | undefined;
        if (row) {
          const entries = buildWholeRowEditEntries(rowKey, row);
          stateActions.startEditingBatch(entries);
        }
      }
    },
    [tableState.editingRows, displayData, resolveRowKey, stateActions, buildWholeRowEditEntries]
  );

  const handleEditChange = useCallback(
    (rowKey: string, field: string, value: unknown) => {
      const currentEdits = tableState.editingRows.get(rowKey) ?? {};
      const patch: Record<string, unknown> = { [field]: value };

      const original = displayData.find((r, idx) => {
        const rec = r as Record<string, unknown>;
        return resolveRowKey(rec, idx) === rowKey;
      }) as Record<string, unknown> | undefined;

      // Merge original row first so compute rules can read all source fields
      // (e.g. soldShares from API when the user only changed salePrice)
      let updatedRow: Record<string, unknown> = { ...(original ?? {}), ...currentEdits, ...patch };

      const computedRow = applyComputeRules(updatedRow, field);
      if (computedRow !== updatedRow) {
        const preComputeRow = updatedRow;
        updatedRow = computedRow;
        for (const [k, v] of Object.entries(computedRow)) {
          if (k !== field && v !== preComputeRow[k]) {
            patch[k] = v;
          }
        }
      }

      let mergedRow: Record<string, unknown> = original
        ? { ...original, ...updatedRow }
        : { ...updatedRow };

      if (patchEditedRow) {
        mergedRow = patchEditedRow(mergedRow, { field, value });
        for (const [k, v] of Object.entries(mergedRow)) {
          const prior = original
            ? { ...original, ...currentEdits, ...patch }[k]
            : { ...currentEdits, ...patch }[k];
          if (v !== prior) {
            patch[k] = v;
            updatedRow = { ...updatedRow, [k]: v };
          }
        }
      }

      if (validateEditedRow && stateActions.setRowEditErrors) {
        stateActions.setRowEditErrors(rowKey, validateEditedRow(mergedRow));
      }

      const col = internalSchema.columns.find((c) => c.field === field);
      if (col && col.clearOnEdit.length > 0) {
        col.clearOnEdit.forEach((clearField) => {
          patch[clearField] = null;
          updatedRow = { ...updatedRow, [clearField]: null };
        });
      }

      stateActions.patchEditRow(rowKey, patch);

      if (shouldValidateOnChange && validateEditedRow && stateActions.setRowEditErrors) {
        stateActions.setRowEditErrors(rowKey, validateEditedRow(mergedRow, rowKey));
      }

      if (asyncCellLookup) {
        asyncCellLookup(field, value, updatedRow).then((result) => {
          if (result) {
            stateActions.patchEditRow(rowKey, result);
          }
        }).catch(() => {});
      }
    },
    [
      stateActions,
      tableState.editingRows,
      applyComputeRules,
      internalSchema.columns,
      displayData,
      resolveRowKey,
      patchEditedRow,
      validateEditedRow,
      shouldValidateOnChange,
      asyncCellLookup,
    ]
  );

  const handleEditBlur = useCallback((_rowKey: string, _field: string) => {
    // Placeholder for future Yup validation on blur
  }, []);

  const subRowDataField = internalSchema.features.rows.subRows?.dataField;

  const handleSubRowCellDoubleClick = useCallback(
    (parentKey: string, _subIndex: number, _field: string) => {
      if (tableState.editingRows.has(parentKey)) return;

      const parentRow = displayData.find((r, idx) => {
        const rec = r as Record<string, unknown>;
        return resolveRowKey(rec, idx) === parentKey;
      }) as Record<string, unknown> | undefined;

      if (!parentRow) return;

      const entries = buildWholeRowEditEntries(parentKey, parentRow);
      stateActions.startEditingBatch(entries);
    },
    [tableState.editingRows, displayData, resolveRowKey, stateActions, buildWholeRowEditEntries]
  );

  const handleSubRowEditChange = useCallback(
    (parentKey: string, subIndex: number, field: string, value: unknown) => {
      const subRowKey = `${parentKey}.${subIndex}`;
      stateActions.updateEditValue(subRowKey, field, value);

      const currentEdits = tableState.editingRows.get(subRowKey) ?? {};
      const originalParent = displayData.find((r, idx) => {
        const rec = r as Record<string, unknown>;
        return resolveRowKey(rec, idx) === parentKey;
      }) as Record<string, unknown> | undefined;
      const originalSubRow = originalParent && subRowDataField
        ? ((originalParent[subRowDataField] as unknown[] | undefined)?.[subIndex] as Record<string, unknown> | undefined)
        : undefined;
      const updatedRow = { ...(originalSubRow ?? {}), ...currentEdits, [field]: value };
      const computedRow = applyComputeRules(updatedRow, field);
      if (computedRow !== updatedRow) {
        Object.entries(computedRow).forEach(([k, v]) => {
          if (k !== field && v !== updatedRow[k]) {
            stateActions.updateEditValue(subRowKey, k, v);
          }
        });
      }

      if (
        shouldValidateOnChange &&
        validateEditedRow &&
        stateActions.setRowEditErrors &&
        subRowDataField
      ) {
        const parentRow = displayData.find((r, idx) => {
          const rec = r as Record<string, unknown>;
          return resolveRowKey(rec, idx) === parentKey;
        }) as Record<string, unknown> | undefined;
        const originalSubRow = parentRow
          ? ((parentRow[subRowDataField] as unknown[] | undefined)?.[subIndex] as
              | Record<string, unknown>
              | undefined)
          : undefined;
        const mergedSubRow = originalSubRow
          ? { ...originalSubRow, ...(computedRow !== updatedRow ? computedRow : updatedRow) }
          : computedRow !== updatedRow
          ? computedRow
          : updatedRow;
        stateActions.setRowEditErrors(subRowKey, validateEditedRow(mergedSubRow, subRowKey));
      }

      const col = internalSchema.subRowColumns.find((c) => c.field === field);
      if (col && col.clearOnEdit.length > 0) {
        col.clearOnEdit.forEach((clearField) => {
          stateActions.updateEditValue(subRowKey, clearField, null);
        });
      }
    },
    [
      stateActions,
      tableState.editingRows,
      applyComputeRules,
      internalSchema.subRowColumns,
      validateEditedRow,
      shouldValidateOnChange,
      subRowDataField,
      displayData,
      resolveRowKey,
    ]
  );

  const handleSubRowEditBlur = useCallback(
    (_parentKey: string, _subIndex: number, _field: string) => {
      // Placeholder for future Yup validation on blur
    },
    []
  );

  const editingSubRows = useMemo(() => {
    const map = new Map<string, Record<string, unknown>>();
    tableState.editingRows.forEach((values, key) => {
      if (key.includes('.')) {
        map.set(key, values);
      }
    });
    return map;
  }, [tableState.editingRows]);

  const subRowEditErrors = useMemo(() => {
    const map = new Map<string, Record<string, string>>();
    tableState.editErrors.forEach((errors, key) => {
      if (key.includes('.')) {
        map.set(key, errors);
      }
    });
    tableState.serverErrors.forEach((errors, key) => {
      if (key.includes('.')) {
        const existing = map.get(key) ?? {};
        map.set(key, { ...existing, ...errors });
      }
    });
    return map;
  }, [tableState.editErrors, tableState.serverErrors]);

  return {
    buildWholeRowEditEntries,
    getSubRowKeysForParent,
    handleCellDoubleClick,
    handleEditChange,
    handleEditBlur,
    subRowDataField,
    handleSubRowCellDoubleClick,
    handleSubRowEditChange,
    handleSubRowEditBlur,
    editingSubRows,
    subRowEditErrors,
  };
}
